import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ListOrdered, Minus, Search, Target, TrendingDown, TrendingUp, Flame } from "lucide-react";
import type { LeaderboardRowDto } from "@/types/api";
import { standingsApi } from "@/api/standings";
import { useScope } from "@/app/useScope";
import { PointsBreakdownModal } from "@/components/PointsBreakdownModal";
import { RequireContext, ErrorState, PageLoader, EmptyState } from "@/components/StateViews";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export function LeaderboardPage() {
  return (
    <RequireContext>
      <Leaderboard />
    </RequireContext>
  );
}

function Leaderboard() {
  const scope = useScope();
  const [filter, setFilter] = useState("");
  const [breakdown, setBreakdown] = useState<{ userId: string; name: string } | null>(null);

  const q = useQuery({
    queryKey: ["leaderboard", scope.leagueId, scope.tournamentId],
    queryFn: () => standingsApi.leaderboard(scope),
  });

  const rows = useMemo(() => {
    const data = q.data ?? [];
    if (!filter.trim()) return data;
    const f = filter.toLowerCase();
    return data.filter((r) => r.fullName.toLowerCase().includes(f));
  }, [q.data, filter]);

  const leader = q.data?.[0]?.totalPoints ?? 0;

  if (q.isLoading) return <PageLoader />;
  if (q.error) return <ErrorState error={q.error} />;
  if ((q.data ?? []).length === 0)
    return <EmptyState title="Üres a tabella" description="Amint valaki leadja az első tippjét, megjelenik itt." icon={<ListOrdered className="size-7" />} />;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
          <ListOrdered className="size-5 text-primary" /> Játék tabella
        </h1>
        <div className="relative sm:w-64">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Szűrés névre…" className="pl-8" />
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border">
        <div className="hidden gap-2 bg-muted/50 px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground sm:flex">
          <span className="w-14">Hely</span>
          <span className="flex-1">Név</span>
          <span className="w-16 text-center">🎯</span>
          <span className="w-20 text-center">Sorozat</span>
          <span className="w-24 text-center">Pont</span>
          <span className="w-20 text-center">Lemaradás</span>
        </div>
        <ul className="divide-y divide-border/60">
          {rows.map((r) => (
            <LeaderRow key={r.userId} row={r} leaderPoints={leader} onClickPoints={() => setBreakdown({ userId: r.userId, name: r.fullName })} />
          ))}
        </ul>
      </div>

      <PointsBreakdownModal
        open={!!breakdown}
        onClose={() => setBreakdown(null)}
        userId={breakdown?.userId}
        playerName={breakdown?.name}
      />
    </div>
  );
}

function Delta({ row }: { row: LeaderboardRowDto }) {
  if (row.previousRank === null) return <Minus className="size-3.5 text-muted-foreground" />;
  const diff = row.previousRank - row.rank;
  if (diff > 0) return <span className="inline-flex items-center text-success"><TrendingUp className="size-3.5" />{diff}</span>;
  if (diff < 0) return <span className="inline-flex items-center text-destructive"><TrendingDown className="size-3.5" />{-diff}</span>;
  return <Minus className="size-3.5 text-muted-foreground" />;
}

function LeaderRow({ row, leaderPoints, onClickPoints }: { row: LeaderboardRowDto; leaderPoints: number; onClickPoints: () => void }) {
  const behind = leaderPoints - row.totalPoints;
  return (
    <li className={cn("flex items-center gap-2 px-3 py-2.5 text-sm", row.isMe && "bg-primary/5")}>
      <div className="flex w-14 shrink-0 items-center gap-1">
        <span className="font-semibold tabular-nums">{row.rank}.</span>
        <span className="text-xs"><Delta row={row} /></span>
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <span className={cn("truncate font-medium", row.isMe && "text-primary")}>
          {row.fullName} {row.isMe && <span className="text-xs text-muted-foreground">(te)</span>}
        </span>
        {/* Mobil: a statok a név alatt */}
        <span className="flex items-center gap-2 text-xs text-muted-foreground sm:hidden">
          <span className="inline-flex items-center gap-0.5"><Target className="size-3" />{row.bullseyeCount}</span>
          {row.currentStreak > 0 && <span className="inline-flex items-center gap-0.5 text-warning"><Flame className="size-3" />{row.currentStreak}</span>}
          {behind > 0 && <span>−{behind}</span>}
        </span>
      </div>

      <span className="hidden w-16 text-center tabular-nums sm:block">{row.bullseyeCount}</span>
      <span className="hidden w-20 justify-center gap-1 sm:flex">
        {row.currentStreak > 0 ? (
          <span className="inline-flex items-center gap-0.5 text-warning"><Flame className="size-3.5" />{row.currentStreak}</span>
        ) : (
          <span className="text-muted-foreground">★ {row.longestStreak}</span>
        )}
      </span>

      <button
        type="button"
        onClick={onClickPoints}
        className="w-20 shrink-0 rounded-md py-1 text-center font-semibold tabular-nums text-primary transition-colors hover:bg-primary/10 sm:w-24"
        title="Pontok részletezése"
      >
        {row.totalPoints}
      </button>

      <span className="hidden w-20 text-center text-muted-foreground tabular-nums sm:block">{behind > 0 ? `−${behind}` : "—"}</span>
    </li>
  );
}
